import * as React from 'react'
import { connect } from 'react-redux'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { Icon, IconType } from '@amzn/dvui/dist/Icon'
import { Tooltip } from '@amzn/dvui/dist/Tooltip'
import * as cn from 'classnames'
import { DecodedText } from '../decodedText/decodedText'
import {
  TextLocalizer,
  withLocalization,
  LocalizerProps
} from '../localizer/localizer'
import { StringId } from '../localizer/stringId'
import { Synopsis } from '../synopsis/synopsis'
import { ResponsiveImage } from '../images/responsiveImage'
import { Rating } from '../badges/rating'
import { EpisodePlayback } from '../episodePlayback/episodePlayback'
import { ActionBox } from '../actionBox/actionBox'
import { isWatched } from '../../utils/getPlaybackProgress'
import { hasAny } from '../../utils/utils'
import { getFirstPlaybackData } from '../../utils/playbackActionHelper'
import * as st from './episode.module.scss'
import * as utilityClasses from '../utilityClasses.module.scss'
import { widgetNameClasses } from '../../utils/widgetNameClasses'
import { SrcSize } from '../../utils/imageUtils'
import {
  getTvodWinnersChildren,
  getMoreWaysToWatchChildren
} from '../../selectors/acquisitionActions'
import { isPurchaseAction } from '../../utils/getAcquisitionActionChildren'
import { CollectionType } from '../collection/collectionTypes'
import { IDetailPageState } from '../../reducers'
import { sanitizedRequestContextSelector } from '../../selectors/requestContext'
import { purchaseSelector } from '../../selectors/purchase'
import { BTF_DECORATION_SCHEME } from '../../reducers/action'
import { DETAIL_FRAGMENT } from '../../reducers/detail'
import { playerConfigDataSelector } from '../../selectors/player'
import { DownloadAction } from '@shared/SPA/downloadButton/DownloadAction'
import { DownloadButton } from '@shared/SPA/downloadButton/DownloadButton'
import { getFirstDownloadData } from '../../utils/downloadActionHelper'
import { isDeeplinkableActionReadySelector } from '../../selectors/pageContext'
import { processDeeplinkFor } from '@shared/deeplink'
import { safeBool } from '@utils'

const PACKSHOT_WIDTH_DESKTOP = 268
const PACKSHOT_WIDTH_MOBILE = 148

interface OwnProps {
  collectionType: CollectionType
  titleId: string
  epListIndex: string
  isFirstEpisode: boolean
  isBonus: boolean
}

type StateProps = ReturnType<typeof mapStateToProps>
type Props = StateProps & OwnProps & LocalizerProps

interface IPackshot {
  packshotUrl?: string
  isWatched: boolean
}

const buildTooltip = (titleStringId: StringId, content: string, id: string) => {
  const title = <TextLocalizer stringID={titleStringId} />
  const tooltipContent = (
    <>
      <div className={st.tooltipTitle}>{title}</div>
      <span className={st.tooltipContent}>
        <DecodedText string={content} />
      </span>
    </>
  )

  return (
    <Tooltip content={tooltipContent} position="top" size="m" tabIndex={0} id={id}>
      {title}
    </Tooltip>
  )
}

function Packshot({ packshotUrl, isWatched }: IPackshot) {
  if (!packshotUrl) {
    return (
      <div className={`${st.packshot} ${st.fallbackPackshot}`}>
        <Icon use={IconType.SMILE} />
      </div>
    )
  }

  const imgProps = {
    alt: '',
    src: packshotUrl,
    baseValue: PACKSHOT_WIDTH_DESKTOP,
    srcSizes: [
      { 'max-phablet': PACKSHOT_WIDTH_MOBILE },
      { 'min-desktop': PACKSHOT_WIDTH_DESKTOP }
    ] as SrcSize[]
  }

  const classNames = cn(st.packshot, { [st.packshotDimmed]: isWatched })
  return (
    <div className={classNames}>
      <ResponsiveImage {...imgProps} />
    </div>
  )
}

export const ConnectedEpisode = (props: Props) => {
  const {
    playerConfigData,
    customerPreferences,
    detail,
    action,
    titleId,
    epListIndex,
    getString,
    isFirstEpisode,
    isBonus,
    pageTitleId,
    requestContext: { rawContext },
    purchaseDetails,
    isRefunding,
    isDeeplinkableActionReady,
    downloadLaunchType,
  } = props

  const {
    title,
    ratingBadge,
    subtitles,
    audioTracks,
    images,
    releaseDate,
    runtime,
    synopsis
  } = detail

  const playbackActions = action && action.playbackActions
  const downloadActions = action && action.downloadActions
  const notificationActions = action && action.notificationActions
  const watchPartyAction = action && action.watchPartyAction
  const acquisitionActions = action && action.acquisitionActions
  const playbackData = getFirstPlaybackData(playbackActions)
  const downloadData = getFirstDownloadData(downloadActions)
  const hasPlayback = Boolean(playbackData)
  const sequenceNumber = detail && detail.episodeNumber

  const playbackProps = {
    title,
    playbackActions,
    playerConfigData,
    sequenceNumber,
    notificationActions,
    entityType: detail && detail.entityType,
    isBonus,
    titleId,
    purchaseDetails,
    isRefunding,
    isDeeplinkableActionReady,
    duration: detail && detail.duration,
  }

  const onClick = (downloadLaunchType === 'NONE' || downloadLaunchType === 'EMBEDDED') ? undefined: (e: React.MouseEvent) => {
    e.preventDefault()
    processDeeplinkFor[downloadLaunchType]({url: downloadData.downloadURL, fallbackUrl: downloadData.fallbackURL})
  }

  const downloadInfo = downloadData && {
    ...downloadData,
    isMovie: false,
    episodeNumber: sequenceNumber,
    onClick,
  }

  const packshotProps = {
    packshotUrl: images && images.packshot,
    isWatched: Boolean(playbackData && isWatched(playbackData.progress))
  }

  const separator = getString(StringId.DV_comma_separator)
  const subtitlesFormatted = (subtitles || []).join(separator)
  const audioTracksFormatted = (audioTracks || []).join(separator)

  const tvodAsins = getTvodWinnersChildren(
    acquisitionActions && acquisitionActions.tvodWinners
  )
    .map(
      (action: WpsRuntime.atv.wps.PurchaseActionProps) => action.asin || null
    )
    .filter(Boolean)

  const mwtwAsins = getMoreWaysToWatchChildren(
    acquisitionActions && acquisitionActions.moreWaysToWatch
  )
    .map(
      (action: WpsRuntime.atv.wps.AcquisitionActionProps) =>
        (isPurchaseAction(action) && action.asin) || null
    )
    .filter(Boolean)

  const asins = Array.from(new Set([...mwtwAsins, ...tvodAsins]))
  const hasDownload = Boolean(downloadInfo)

  const epWrapperClasses = cn(st.episode, 'js-node-episode-container', {
    [st.episodeWithPlayback]: hasPlayback,
    [st.episodeWithDownload]: hasDownload,
  })

  const isPurchasing = Boolean(purchaseDetails)

  const actionBoxProps = {
    notificationActions,
    acquisitionActions,
    sequenceNumber,
    pageTitleId,
    titleID: titleId,
    isPurchasing,
    isRefunding,
    customerPreferences: customerPreferences || undefined,
    rawContext,
    watchPartyAction
  }

  const id = `av-ep-${epListIndex}`

  return (
    <li id={id} data-aliases={asins.join(',')} className={epWrapperClasses}>
      <input
        id={`selector-${titleId}`}
        name="highlight-list-selector"
        className={st.expanderToggleInput}
        type="checkbox"
        defaultChecked={isFirstEpisode && !isBonus}
      />
      <label className={st.expanderToggle} htmlFor={`selector-${titleId}`} />
      <label className={st.expanderToggleChevron} htmlFor={`selector-${titleId}`}>
        {/* also used in tvodButton scrollTopIntoView */}
        <Icon use={IconType.BREADCRUMB_CHEVRON} className={st.chevron} />
      </label>
      <div data-automation-id={`ep-title-${props.epListIndex}`} className={st.episodeTitle}>
        <EpisodePlayback {...playbackProps} />
        {isDeeplinkableActionReady && hasDownload &&
          (global.__NATIVEAPP__ ? (
            <DownloadAction downloadInfo={downloadInfo!} />
          ) : (
            <DownloadButton {...downloadInfo!} />
          ))}
      </div>
      <div className={`${st.tags} ${st.expandable}`}>
        <div>{releaseDate}</div>
        <div>{runtime}</div>
        <div className={st.regulatoryRatingContainer}>
          <Rating ratingBadge={ratingBadge} />
        </div>
        {hasAny(subtitles) && (
          <div className={st.subtitles}>
            {buildTooltip(StringId.DV_TW_title_subtitles, subtitlesFormatted, `subt-${id}`)}
          </div>
        )}
        {hasAny(audioTracks) && (
          <div className={st.languages}>
            {buildTooltip(StringId.DV_TW_title_languages, audioTracksFormatted, `lang-${id}`)}
          </div>
        )}
      </div>
      <div className={st.expandable}>
        <Packshot {...packshotProps} />
      </div>
      <div className={`${st.synopsis} ${st.expandable}`}>
        <Synopsis synopsis={synopsis} numOfLines={2} expanderId={`synopsis-${titleId}`} />
      </div>
      <div
        className={`${widgetNameClasses.offers} ${st.actionBox} ${utilityClasses.fullWidth} ${st.expandable}`}>
        <ActionBox {...actionBoxProps} />
      </div>
    </li>
  )
}

const getPurchaseDetails = (state: IDetailPageState, titleId: string) => {
  const purchase = purchaseSelector(state)
  return purchase &&
    (!purchase.details ||
      purchase.details.canonicalId === titleId ||
      safeBool(purchase.details.offer.purchaseData.isSeasonPurchase))
    ? purchase
    : null
}

const mapStateToProps = (
  state: IDetailPageState,
  ownProps: OwnProps
) => ({
  playerConfigData: playerConfigDataSelector(state),
  action: state.action[BTF_DECORATION_SCHEME][ownProps.titleId],
  detail: state.detail[DETAIL_FRAGMENT][ownProps.titleId]!,
  customerPreferences: state.customerPreferences,
  requestContext: sanitizedRequestContextSelector(state),
  pageTitleId: state.pageTitleId,
  purchaseDetails: getPurchaseDetails(state, ownProps.titleId),
  isRefunding: Boolean(state.refund.refunding && (state.refund.refunding.titleId === ownProps.titleId)),
  isDeeplinkableActionReady: isDeeplinkableActionReadySelector(state),
  downloadLaunchType: state.pageContext.downloadLaunchType,
})

export const Episode = withLocalization(
  connect(mapStateToProps)(ConnectedEpisode)
)
